import { client } from '@utils/graphql/setup';
import {
  QueryCreator,
  CriteriaCreator,
  MutationCreator,
  SingleRecordMutationCreator,
} from './base';
import { createAcceptedProperties, filterData } from '../helpers';
import zonesData from '../mocks/zones.json';
import * as is from 'is';
import { deleteStream, getStreams } from './streams';
import { getLogger } from '@utils/general';
import { deleteApiKey, getApiKeys } from './apiKeys';
const logger = getLogger('src.utils.graphQL.creators.zones');

const acceptedReturnProperties = createAcceptedProperties(
  'id',
  'name',
  'description',
  'auto_offline_enabled',
  'include_in_current_status_report',
  'last_updated'
);

const acceptedCriteriaProperties = {
  name: { type: 'string' },
  description: { type: 'string', triplequoted: true },
  last_updated: { type: 'string' },
  auto_offline_enabled: { type: 'boolean' },
  include_in_current_status_report: { type: 'boolean' },
  or: { type: 'or' },
  and: { type: 'and' },
  not: { type: 'not' },
};

class ZonesCriteria extends CriteriaCreator {
  constructor(criteria = {}) {
    super(criteria);
  }
  get acceptedCriteria() {
    return acceptedCriteriaProperties;
  }
}
export class ZonesQuery extends QueryCreator {
  constructor(queryType = 'zones', options = {}) {
    super(queryType, ZonesCriteria);

    if (options?.pagination) {
      const { count_per_page, start_on_page } = options.pagination;
      if (count_per_page) this.addPagination('countPerPage', count_per_page);
      if (start_on_page) this.addPagination('startOnPage', start_on_page);
      this.addPaginationReturn('total_pages', 'total_records', 'count_per_page', 'current_page');
    }

    if (options?.sort) {
      this.setSort(options.sort.field, options.sort.direction);
    }
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

export function getZones(id, criteria = {}, options = {}) {
  let query;
  if (id) {
    query = new ZonesQuery('zone');
    query.setId(id);
  } else {
    query = new ZonesQuery('zones', options);
    if (!is.empty(criteria)) {
      for (const [key, value] of Object.entries(criteria)) {
        query.addCriteria(key, value);
      }
    }
  }
  return {
    query,
    execute() {
      return /^dev(?:elop(?:ment)?)?$/i.test(import.meta.env.VITE_ENV)
        ? Promise.resolve().then(() => {
          return { data: filterData(zonesData, criteria) };
        })
        : client.query(query.name, query.toString());
    },
  };
}

// Mutations
export class ZonesMutation extends MutationCreator {
  constructor(queryType = 'create_zone', criteria = {}) {
    super(queryType, ZonesCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

export class SingleZoneMutation extends SingleRecordMutationCreator {
  constructor(queryType = 'create_zone', criteria = {}) {
    super(queryType, ZonesCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

export function saveZone(mutationType, input) {
  let mutation;
  mutation = new ZonesMutation(mutationType, input);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export function updateZone(mutationType, id, input = {}) {
  let mutation;
  mutation = new SingleZoneMutation(mutationType, input);
  mutation.setId(id);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export async function deleteZone(zone) {
  // @todo: Since all remove_xxx only accept ids as the input, we can probably change
  // all delete functions to only need an id instead of the entire data object
  const { execute: executeZoneRemoval } = updateZone('remove_zone', zone.id);

  const { execute: executeGetStreams, query: streamQuery } = getStreams(null, { zoneId: zone.id });
  streamQuery.addReturnProperties('id', 'name');

  const { execute: executeGetApiKeys, query: apiKeyQuery } = getApiKeys(null, { zoneId: zone.id });
  apiKeyQuery.addReturnProperties('id', 'user_key');

  try {
    const { error: getStreamsError, data: streamsAssocWithZone } = await executeGetStreams();
    if (getStreamsError) {
      throw new Error(getStreamsError);
    }

    if (streamsAssocWithZone) {
      for (const stream of streamsAssocWithZone) {
        await deleteStream(stream);
      }
    }

    const { error: getApiKeysError, data: apiKeysAssocWithZone } = await executeGetApiKeys();

    if (getApiKeysError) {
      throw new Error(getApiKeysError);
    }
    if (apiKeysAssocWithZone) {
      for (const apiKey of apiKeysAssocWithZone) {
        await deleteApiKey(apiKey);
      }
    }

    await executeZoneRemoval();
  }
  catch (error) {
    logger.error(error, `Failed to delete zone with ID: ${zone.id}.`);
    throw new Error(error);
  }
  return;
}
